import { api } from '../api.js';
import { el, clear } from '../dom.js';
import { t } from '../i18n.js';

// Where the event happens.
//
// Three rules from the roadmap, and all three are in this one control because
// splitting them is how the third gets forgotten:
//
// 1. **The account's address is the default.** A club never types its address
//    again; an open air overrides it once. The default arrives as a value, not
//    as a lookup the renderer repeats — the series stores the id it was given,
//    so a venue that moves does not rewrite its own history.
// 2. **An account with no address has no default**, so the same control is a
//    required field rather than a toggle. That is a person account, and it is
//    the half that breaks when this is built once for venues only.
// 3. **A different address is an address-book entry, not free text.**
//    Autocomplete from the first character, creating one a deliberate second
//    step — the same shape as the tag field, for the same reason: without it
//    there are eight spellings of "Hofgarten" inside a year.
export function placeField({ account = null, series = null, idPrefix = 'pf' } = {}) {
  const fallback = account?.defaultPlace ?? null;

  // What the series already points at, when that is not simply the default.
  const initial = series?.placeId && series.placeId !== fallback?.id
    ? { id: series.placeId, name: series.placeName, address: series.placeAddress }
    : null;

  let chosen = initial;

  const elsewhere = el('input', { id: `${idPrefix}-elsewhere`, type: 'checkbox' });
  elsewhere.checked = Boolean(initial);

  const defaultLine = el('p', { class: 'card-meta' }, fallback
    ? [fallback.name, fallback.address ? ` · ${fallback.address}` : ''].join('')
    : '');

  const toggleRow = el('div', { class: 'field field-check' }, [
    elsewhere, el('label', { for: `${idPrefix}-elsewhere` }, t.placeElsewhere),
  ]);

  const picked = el('p', { class: 'place-picked' });

  const input = el('input', {
    id: `${idPrefix}-search`,
    type: 'text',
    class: 'tag-input',
    autocomplete: 'off',
    placeholder: t.placePlaceholder,
    'aria-describedby': `${idPrefix}-hint`,
    role: 'combobox',
    'aria-expanded': 'false',
    'aria-controls': `${idPrefix}-list`,
  });

  const list = el('ul', { class: 'tag-suggestions', role: 'listbox', id: `${idPrefix}-list` });
  list.hidden = true;

  const creator = creatorForm();
  creator.node.hidden = true;

  const picker = el('div', { class: 'place-picker' }, [
    picked,
    el('div', { class: 'tag-input-wrap' }, [input, list]),
    creator.node,
    el('p', { class: 'card-meta', id: `${idPrefix}-hint` }, t.placeHint),
  ]);

  const node = el('div', { class: 'field place-field' }, [
    el('span', { class: 'rec-label' }, t.fieldPlace),
    fallback ? defaultLine : null,
    fallback ? toggleRow : null,
    picker,
  ]);

  elsewhere.addEventListener('change', () => {
    if (!elsewhere.checked) chosen = null;
    paint();
    if (elsewhere.checked) input.focus();
  });

  paint();

  function paint() {
    // With no default there is nothing to toggle back to: the picker is the
    // field, and it is required.
    picker.hidden = Boolean(fallback) && !elsewhere.checked;
    defaultLine.hidden = Boolean(fallback) && elsewhere.checked;

    clear(picked);
    if (chosen) {
      const drop = el('button', {
        type: 'button', class: 'tag-chip-remove', 'aria-label': `${chosen.name} ${t.remove}`,
      }, '×');
      drop.addEventListener('click', () => {
        chosen = null;
        paint();
        input.focus();
      });
      picked.append(el('span', { class: 'tag-chip' }, [
        chosen.address ? `${chosen.name} · ${chosen.address}` : chosen.name,
        drop,
      ]));
    }
    input.hidden = Boolean(chosen);
  }

  function choose(place) {
    chosen = place;
    input.value = '';
    closeList();
    creator.close();
    paint();
    node.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function closeList() {
    list.hidden = true;
    input.setAttribute('aria-expanded', 'false');
    clear(list);
  }

  let token = 0;
  async function refresh() {
    const query = input.value.trim();
    const mine = ++token;

    let found = [];
    try {
      found = await api.get(`/places?q=${encodeURIComponent(query)}`);
    } catch {
      found = [];
    }
    if (mine !== token) return; // a later keystroke already answered

    clear(list);
    for (const place of found) {
      list.append(option(place.address ? `${place.name} · ${place.address}` : place.name,
        t.placeExisting, () => choose(place)));
    }

    // Offered last and labelled as what it is — the same ordering as the tag
    // field, and for the same reason: putting "create" first is how a second
    // spelling of an address that already exists gets written.
    if (query) {
      list.append(option(query, t.placeCreate, () => creator.open(query), 'tag-suggestion-new'));
    }

    list.hidden = list.children.length === 0;
    input.setAttribute('aria-expanded', list.hidden ? 'false' : 'true');
  }

  function option(label, note, onPick, extra = '') {
    const item = el('li', { class: `tag-suggestion ${extra}`.trim(), role: 'option' }, [
      el('span', { class: 'tag-suggestion-name' }, label),
      el('span', { class: 'tag-suggestion-note' }, note),
    ]);
    item.addEventListener('mousedown', (event) => {
      event.preventDefault(); // keep focus in the input
      onPick();
    });
    return item;
  }

  input.addEventListener('input', refresh);
  input.addEventListener('focus', refresh);
  input.addEventListener('blur', () => setTimeout(closeList, 120));
  input.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') {
      // Never submits the form: Enter here means "take this entry", and a
      // wizard that advances because somebody finished typing an address is a
      // wizard that skips the rest of the slide.
      event.preventDefault();
      list.querySelector('.tag-suggestion:not(.tag-suggestion-new)')
        ?.dispatchEvent(new MouseEvent('mousedown'));
      return;
    }
    if (event.key === 'Escape' && !list.hidden) {
      event.preventDefault();
      closeList();
    }
  });

  // Creating an entry is a form of its own, not a side effect of pressing
  // Enter on a name nobody recognised.
  function creatorForm() {
    const name = el('input', { id: `${idPrefix}-new-name`, type: 'text', autocomplete: 'off' });
    const address = el('input', { id: `${idPrefix}-new-address`, type: 'text', autocomplete: 'off' });
    const city = el('select', { id: `${idPrefix}-new-city` });
    const error = el('p', { class: 'pub-error', role: 'alert' });
    error.hidden = true;

    const confirm = el('button', { class: 'button', type: 'button' }, t.placeCreateAction);
    const abort = el('button', { class: 'button button-quiet', type: 'button' }, t.cancelAction);

    const body = el('div', { class: 'place-creator' }, [
      el('p', { class: 'rec-label' }, t.placeNewHeading),
      error,
      field(`${idPrefix}-new-name`, t.fieldPlaceName, name),
      field(`${idPrefix}-new-address`, t.fieldAddress, address),
      field(`${idPrefix}-new-city`, t.fieldCity, city),
      el('div', { class: 'row' }, [confirm, abort]),
    ]);

    let loaded = false;
    async function cities() {
      if (loaded) return;
      loaded = true;
      try {
        const rows = await api.get('/cities');
        clear(city);
        city.append(el('option', { value: '' }, t.fieldNone));
        for (const row of rows) city.append(el('option', { value: row.id }, row.name));
        if (account?.cityId) city.value = String(account.cityId);
      } catch {
        // A place without a city is a legitimate row; the field simply stays
        // empty rather than blocking the create.
      }
    }

    abort.addEventListener('click', () => close());
    confirm.addEventListener('click', async () => {
      if (!name.value.trim()) {
        error.textContent = t.placeNameRequired;
        error.hidden = false;
        return;
      }
      confirm.disabled = true;
      try {
        const created = await api.post('/places', {
          name: name.value.trim(),
          address: address.value.trim() || null,
          cityId: city.value ? Number(city.value) : null,
        });
        choose(created);
      } catch {
        error.textContent = t.placeCreateFailed;
        error.hidden = false;
      } finally {
        confirm.disabled = false;
      }
    });

    function close() {
      body.hidden = true;
      error.hidden = true;
    }

    return {
      node: body,
      close,
      open(prefill) {
        name.value = prefill;
        address.value = '';
        error.hidden = true;
        body.hidden = false;
        cities().then(() => name.focus());
      },
    };
  }

  return {
    node,
    value: () => (chosen ? chosen.id : (elsewhere.checked ? null : fallback?.id ?? null)),
    // Only where there is nothing to fall back to. An institution always has an
    // answer; a person has to give one.
    required: !fallback,
  };
}

function field(id, label, control) {
  return el('div', { class: 'field' }, [el('label', { for: id }, label), control]);
}
